"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TutorialScanHelper = void 0;
class TutorialScanHelper {
    static async scanSourceFile(force = false) {
        var _a, _b;
        // AI source: no file involved - the scan expands the data already in the form
        const aiMode = this.form.source === 'ai';
        const useKnowledge = !aiMode && !!this.form.scanKnowledgeId && !((_a = this.form.sourceFile) === null || _a === void 0 ? void 0 : _a.path);
        const useSavedSourceFile = force && !!this.tutorialId && !useKnowledge && !((_b = this.form.sourceFile) === null || _b === void 0 ? void 0 : _b.path);
        if (aiMode) {
            if (!TutorialScanHelper.hasFormData.call(this)) {
                this.popupService.error('tutorial.form.scanNeedsData'.t());
                return false;
            }
        }
        else if (!useKnowledge && (!this.form.sourceFile || (!this.form.sourceFile.path && !useSavedSourceFile))) {
            return false;
        }
        try {
            this.loading = true;
            this.saving = true;
            this.scanSnapshot = aiMode || force ? TutorialScanHelper.createSnapshot.call(this) : null;
            this.steps = [];
            this.scanProgressCurrentSection = 0;
            this.scanProgressTotalSections = 0;
            this.scanProgressPhase = 'reading';
            this.scanProgressSectionTitle = '';
            const formData = TutorialScanHelper.buildScanFormData.call(this, aiMode, useKnowledge, useSavedSourceFile);
            const response = await fetch('/api/tutorial/scan', {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: {
                    'Authorization': `Bearer ${this.apiService.token}`
                }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            if (!response.body) {
                throw new Error('No response body');
            }
            await TutorialScanHelper.readScanStream.call(this, response.body);
            this.scanned = true;
            this.notifyService.notify('tutorial.scanned'.t(), 'info');
            return true;
        }
        catch (error) {
            console.error('Failed to scan tutorial file:', error);
            if (this.scanSnapshot) {
                this.form = this.scanSnapshot.form;
                this.steps = this.scanSnapshot.steps;
                this.updateChatTools();
            }
            this.popupService.error('tutorial.scanFailed'.t());
            return false;
        }
        finally {
            this.scanSnapshot = null;
            this.loading = false;
            this.saving = false;
            this.scanProgressPhase = '';
            this.scanProgressSectionTitle = '';
        }
    }
    static hasFormData() {
        var _a;
        const { name, description, introText, tutorialGoal } = this.form;
        return !!((name === null || name === void 0 ? void 0 : name.trim()) || (description === null || description === void 0 ? void 0 : description.trim()) || (introText === null || introText === void 0 ? void 0 : introText.trim()) || (tutorialGoal === null || tutorialGoal === void 0 ? void 0 : tutorialGoal.trim()) || ((_a = this.steps) === null || _a === void 0 ? void 0 : _a.length));
    }
    static createSnapshot() {
        const sections = new Map();
        this.steps.forEach(step => step.sections.forEach(section => {
            if (section.id) {
                sections.set(section.id.toString(), section);
            }
        }));
        return { form: { ...this.form }, steps: this.steps, sections };
    }
    // Full current form data (without file objects) so the scan can expand on it
    static buildCurrentForm() {
        return {
            name: this.form.name,
            description: this.form.description,
            introText: this.form.introText,
            tutorialGoal: this.form.tutorialGoal,
            active: this.form.active,
            steps: this.steps.map(step => ({
                id: step.id,
                title: step.title,
                description: step.description,
                order: step.order,
                sections: step.sections.map(section => ({
                    id: section.id,
                    title: section.title,
                    description: section.description,
                    prompt: section.prompt,
                    order: section.order,
                    sourceText: section.sourceText,
                    formData: section.formData || '',
                    todoItems: section.todoItems
                }))
            }))
        };
    }
    static buildScanFormData(aiMode, useKnowledge, useSavedSourceFile) {
        var _a;
        const formData = new FormData();
        if (aiMode) {
            formData.append('currentForm', JSON.stringify(TutorialScanHelper.buildCurrentForm.call(this)));
        }
        else {
            if (useKnowledge) {
                formData.append('knowledgeId', this.form.scanKnowledgeId);
            }
            else if ((_a = this.form.sourceFile) === null || _a === void 0 ? void 0 : _a.path) {
                const blob = TutorialScanHelper.base64ToBlob(this.form.sourceFile.path, this.form.sourceFile.mimeType || 'application/pdf');
                formData.append('upload', blob, this.form.sourceFile.filename || 'source.pdf');
            }
            else if (useSavedSourceFile && this.tutorialId) {
                formData.append('tutorialId', this.tutorialId);
                formData.append('useSavedSourceFile', 'true');
            }
            if (this.form.scanPagesFrom != null) {
                formData.append('scanPagesFrom', String(this.form.scanPagesFrom));
            }
            if (this.form.scanPagesTo != null) {
                formData.append('scanPagesTo', String(this.form.scanPagesTo));
            }
        }
        formData.append('tutorialGoal', this.form.tutorialGoal || '');
        if (this.onboardingAttached) {
            formData.append('onboardingAttached', JSON.stringify(this.onboardingAttached));
        }
        return formData;
    }
    // Parses the server-sent event stream and dispatches every complete event
    static async readScanStream(body) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = null;
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
                if (line.startsWith('event:')) {
                    if (currentEvent) {
                        TutorialScanHelper.handleScanEvent.call(this, currentEvent.event, currentEvent.data);
                    }
                    currentEvent = { event: line.slice(6).trim(), data: '' };
                }
                else if (line.startsWith('data:')) {
                    if (currentEvent) {
                        currentEvent.data += (currentEvent.data ? '\n' : '') + line.slice(5).trim();
                    }
                }
                else if (line === '' && currentEvent) {
                    TutorialScanHelper.handleScanEvent.call(this, currentEvent.event, currentEvent.data);
                    currentEvent = null;
                }
            }
        }
        if (currentEvent) {
            TutorialScanHelper.handleScanEvent.call(this, currentEvent.event, currentEvent.data);
        }
    }
    static base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    }
    static handleScanEvent(event, dataStr) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        try {
            const data = JSON.parse(dataStr);
            switch (event) {
                case 'start':
                    this.steps = [];
                    this.scanProgressCurrentSection = 0;
                    this.scanProgressTotalSections = 0;
                    this.scanProgressPhase = 'reading';
                    this.scanProgressSectionTitle = '';
                    break;
                case 'progress':
                    this.scanProgressPhase = data.phase || '';
                    this.scanProgressSectionTitle = data.sectionTitle || '';
                    if (data.totalSections) {
                        this.scanProgressTotalSections = data.totalSections;
                    }
                    if (data.sectionNumber != null) {
                        this.scanProgressCurrentSection = data.sectionNumber;
                    }
                    break;
                case 'index':
                    this.form.name = data.name || this.form.name;
                    this.form.description = data.description || '';
                    this.form.introText = data.introText || '';
                    this.form.active = (_a = data.active) !== null && _a !== void 0 ? _a : true;
                    this.scanProgressTotalSections = data.totalSections || 0;
                    this.form = { ...this.form };
                    break;
                case 'step': {
                    const step = data.step;
                    const stepPayload = {
                        id: (_b = step.id) === null || _b === void 0 ? void 0 : _b.toString(),
                        title: step.title || `${'tutorial.form.step'.t()} ${this.steps.length + 1}`,
                        description: step.description || '',
                        order: step.order || this.steps.length + 1,
                        sections: []
                    };
                    this.steps = [...this.steps, stepPayload];
                    break;
                }
                case 'section': {
                    const section = data.section;
                    const stepPayload = this.steps[data.stepIndex];
                    if (!stepPayload) {
                        break;
                    }
                    // Existing sections (AI source) keep their attachments
                    const previous = section.id ? (_c = this.scanSnapshot) === null || _c === void 0 ? void 0 : _c.sections.get(section.id.toString()) : undefined;
                    stepPayload.sections = [...stepPayload.sections, {
                            id: (_d = section.id) === null || _d === void 0 ? void 0 : _d.toString(),
                            title: section.title || '',
                            description: section.description || '',
                            prompt: section.prompt || '',
                            order: section.order || data.sectionIndex + 1,
                            knowledgeIds: (previous === null || previous === void 0 ? void 0 : previous.knowledgeIds) || [],
                            screenshot: (previous === null || previous === void 0 ? void 0 : previous.screenshot) || null,
                            video: (previous === null || previous === void 0 ? void 0 : previous.video) || null,
                            screenshotMini: (previous === null || previous === void 0 ? void 0 : previous.screenshotMini) || null,
                            videoMini: (previous === null || previous === void 0 ? void 0 : previous.videoMini) || null,
                            sourcePagesFrom: (_g = (_f = (_e = section.sourcePages) === null || _e === void 0 ? void 0 : _e[0]) !== null && _f !== void 0 ? _f : previous === null || previous === void 0 ? void 0 : previous.sourcePagesFrom) !== null && _g !== void 0 ? _g : null,
                            sourcePagesTo: (_k = (_j = (_h = section.sourcePages) === null || _h === void 0 ? void 0 : _h[1]) !== null && _j !== void 0 ? _j : previous === null || previous === void 0 ? void 0 : previous.sourcePagesTo) !== null && _k !== void 0 ? _k : null,
                            sourceText: section.sourceText || '',
                            formData: (previous === null || previous === void 0 ? void 0 : previous.formData) || '',
                            todoItems: (section.todoItems || []).map((todoItem, todoIndex) => {
                                var _a;
                                return ({
                                    title: todoItem.title || '',
                                    description: todoItem.description || '',
                                    done: (_a = todoItem.done) !== null && _a !== void 0 ? _a : false,
                                    order: todoItem.order || todoIndex + 1
                                });
                            })
                        }];
                    this.steps = [...this.steps];
                    break;
                }
                case 'complete':
                    this.scanned = true;
                    break;
                case 'error':
                    throw new Error(data.error || 'Scan failed');
            }
            this.updateChatTools();
        }
        catch (error) {
            console.error('Failed to parse scan event:', error);
            if (event === 'error') {
                throw error;
            }
        }
    }
}
exports.TutorialScanHelper = TutorialScanHelper;
